import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Play, Eye, Lock, CheckCircle, Send, RotateCcw, Unlock, Download, Globe, FileSpreadsheet, Layers, Loader2, X, Trash2, AlertTriangle, Settings, Shield, ShieldOff, Search, Columns, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import PrePayrollValidation from '../components/hr_bench/PrePayrollValidation';
import { downloadCSV } from '../lib/exportUtils';
import { useAuth } from '../context/AuthContext';
import { useActiveCompany } from '../hooks/useActiveCompany';
import CTCGroupTreePicker from '../components/CTCGroupTreePicker';
import GroupRegisterPanel from '../components/GroupRegisterPanel';
import PayrollPreflightPanel from '../components/PayrollPreflightPanel';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PayrollRun = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [periods, setPeriods] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetails, setRunDetails] = useState([]);
  const [staleInfo, setStaleInfo] = useState(null);
  const [miDiag, setMiDiag] = useState(null);
  const [showNewRunModal, setShowNewRunModal] = useState(false);
  // Stage 73.20dc — Delete confirmation modal state.
  const [deleteRunModal, setDeleteRunModal] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [newRunForm, setNewRunForm] = useState({ period_id: '', run_type: 'regular', description: '' });
  const [processing, setProcessing] = useState(false);
  const [activeView, setActiveView] = useState('runs');
  const [globalView, setGlobalView] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [groupFilterRunId, setGroupFilterRunId] = useState(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [alreadyProcessedGroupIds, setAlreadyProcessedGroupIds] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('');  // '' = all periods
  const [ctcGroups, setCtcGroups] = useState([]);
  const [runGroupFilter, setRunGroupFilter] = useState('');  // '' = all groups
  const [asyncJob, setAsyncJob] = useState(null);  // background-job tracker
  const isSuperAdmin = user?.role === 'super_admin';

  // Apply period and group filters on the runs list
  const filteredRuns = useMemo(() => {
    let result = runs;
    if (periodFilter) {
      result = result.filter((r) => String(r.period_id || '') === String(periodFilter));
    }
    if (runGroupFilter) {
      const selectedGroup = ctcGroups.find(g => String(g.id) === String(runGroupFilter));
      const groupCode = selectedGroup?.code;
      const groupName = selectedGroup?.name;
      result = result.filter((r) =>
        String(r.group_id || '') === String(runGroupFilter) ||
        (Array.isArray(r.ctc_group_filter) && (
          r.ctc_group_filter.includes(runGroupFilter) ||
          (groupCode && r.ctc_group_filter.includes(groupCode))
        )) ||
        (groupName && r.group_name === groupName) ||
        (groupCode && r.group_name === groupCode)
      );
    }
    return result;
  }, [runs, periodFilter, runGroupFilter, ctcGroups]);
  const filterPeriodLabel = useMemo(() => {
    if (!periodFilter) return null;
    const p = periods.find((x) => x.id === periodFilter);
    return p?.period_name || p?.period || periodFilter;
  }, [periodFilter, periods]);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      const list = Array.isArray(data) ? data : [];
      setCompanies(list);
      setSelectedCompany((cur) => (list.length > 0 && !cur ? list[0].id : cur));
    } catch (e) { setCompanies([]); console.error(e); }
  }, [setSelectedCompany]);

  const fetchPeriods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/periods?company_id=${selectedCompany}`, { withCredentials: true });
      setPeriods(Array.isArray(data) ? data : []);
    } catch (e) { setPeriods([]); console.error(e); }
  }, [selectedCompany]);

  const fetchCtcGroups = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/controls/ctc-groups?company_id=${selectedCompany}`, { withCredentials: true });
      setCtcGroups(Array.isArray(data) ? data : []);
    } catch (e) { setCtcGroups([]); console.error(e); }
  }, [selectedCompany]);

  const fetchRuns = useCallback(async () => {
    try {
      const url = globalView && isSuperAdmin
        ? `${API_URL}/payroll/runs?global_view=true`
        : `${API_URL}/payroll/runs?company_id=${selectedCompany}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setRuns(Array.isArray(data) ? data : []);
    } catch (e) { setRuns([]); console.error(e); }
  }, [globalView, isSuperAdmin, selectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => { if (selectedCompany) { fetchPeriods(); fetchRuns(); fetchCtcGroups(); } }, [selectedCompany, fetchPeriods, fetchRuns, fetchCtcGroups]);
  useEffect(() => { if (isSuperAdmin && globalView) fetchRuns(); }, [globalView, isSuperAdmin, fetchRuns]);

  const fetchRunDetails = useCallback(async (runId) => {
    try {
      const [detRes, staleRes, diagRes] = await Promise.all([
        axios.get(`${API_URL}/payroll/runs/${runId}/details`, { withCredentials: true }),
        axios.get(`${API_URL}/payroll/runs/${runId}/stale-check`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/payroll/runs/${runId}/monthly-input-diagnostic`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      // Stage 73.20cg — Always normalise details to an array so downstream
      // `.length` / `.map` calls never crash even on partial / 404 / 409
      // backend responses.
      setRunDetails(Array.isArray(detRes.data) ? detRes.data : []);
      setStaleInfo(staleRes.data || null);
      setMiDiag(diagRes.data || null);
    } catch (e) { console.error(e); }
  }, []);

  const handleReprocess = async (runId) => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API_URL}/payroll/runs/${runId}/process`, {}, { withCredentials: true });
      toast.success(`Reprocessed: ${data.total_employees} employees · ${data.exception_count} exceptions · Monthly inputs merged`);
      fetchRuns();
      fetchRunDetails(runId);
      // Refresh selectedRun from server so status/processed_at refresh in the header
      const { data: fresh } = await axios.get(`${API_URL}/payroll/runs/${runId}`, { withCredentials: true });
      setSelectedRun(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reprocess failed');
    } finally { setProcessing(false); }
  };

  const handleCreateRun = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API_URL}/payroll/runs`, { ...newRunForm, company_id: selectedCompany }, { withCredentials: true });
      setShowNewRunModal(false);
      fetchRuns();
      setSelectedRun(data);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleProcessRun = async (runId, groupIds = null) => {
    setProcessing(true);
    try {
      const qs = groupIds && groupIds.length > 0 ? `?ctc_group_ids=${groupIds.join(',')}` : '';
      const { data } = await axios.post(`${API_URL}/payroll/runs/${runId}/process${qs}`, {}, { withCredentials: true });
      const mi = data.monthly_input_rows_applied || 0;
      const filterTag = groupIds?.length ? ` (filtered to ${groupIds.length} CTC group${groupIds.length === 1 ? '' : 's'})` : '';
      toast.success(
        `Processed ${data.total_employees} employees · ${data.exception_count} exceptions` +
        (mi > 0 ? ` · ${mi} monthly input row${mi === 1 ? '' : 's'} merged` : ' · no monthly inputs found for this period') +
        filterTag,
      );
      setShowGroupPicker(false);
      setSelectedGroupIds([]);
      fetchRuns();
      fetchRunDetails(runId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Processing error'); } finally { setProcessing(false); }
  };

  // Stage 73.20dc — Delete Payroll Run.
  // Type-to-confirm the run number, then hit DELETE. Backend cascade-
  // deletes payroll_details / snapshots / versions / statutory_working etc.
  // Blocked server-side for frozen/locked/approved/released/paid runs.
  const handleDeleteRun = async () => {
    if (!deleteRunModal) return;
    const isReleased = ['released', 'locked', 'paid', 'finalized'].includes(deleteRunModal.status) || 
                       (deleteRunModal.payslip_released_count || 0) > 0 || 
                       (deleteRunModal.tax_sheet_released_count || 0) > 0;

    if (isReleased) {
      toast.error("This payroll has already been released. It cannot be deleted.");
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/payroll/runs/${deleteRunModal.id}`,
        { withCredentials: true },
      );
      toast.success('✅ Payroll Run deleted successfully.');
      setDeleteRunModal(null);
      setDeleteConfirmText('');
      if (selectedRun?.id === deleteRunModal.id) setSelectedRun(null);
      fetchRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };



  // ====== ASYNC PROCESS (for large runs >2000 employees) ======
  // Hits /process-async and polls /api/jobs/{id} every 1.5s. Caller stays
  // unblocked and we can show real-time progress instead of a frozen button.
  const handleProcessRunAsync = async (runId, groupIds = null) => {
    try {
      const qs = groupIds && groupIds.length > 0 ? `?ctc_group_ids=${groupIds.join(',')}` : '';
      const { data } = await axios.post(`${API_URL}/payroll/runs/${runId}/process-async${qs}`, {}, { withCredentials: true });
      setAsyncJob({
        job_id: data.job_id,
        run_id: runId,
        status: 'queued',
        total: data.total_employees || 0,
        processed: 0,
        current_step: 'queued',
      });
      setShowGroupPicker(false);
      setSelectedGroupIds([]);
      toast.info(`Background job started — ${data.total_employees} employees queued`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Async processing failed to start');
    }
  };

  // Polling effect for the active async job
  const asyncJobId = asyncJob?.job_id;
  const asyncJobStatus = asyncJob?.status;
  const asyncRunId = asyncJob?.run_id;
  useEffect(() => {
    if (!asyncJobId || ['completed', 'failed'].includes(asyncJobStatus)) return undefined;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/jobs/${asyncJobId}`, { withCredentials: true });
        setAsyncJob(prev => ({
          ...prev,
          status: data.status,
          processed: data.progress?.processed || 0,
          total: data.progress?.total || prev.total,
          current_step: data.progress?.current_step || prev.current_step,
          result: data.result,
          error: data.error,
        }));
        if (data.status === 'completed') {
          clearInterval(interval);
          const r = data.result || {};
          toast.success(`Async run complete: ${r.total_employees} employees · ${r.exception_count || 0} exceptions`);
          fetchRuns();
          fetchRunDetails(asyncRunId);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          toast.error(`Async run failed: ${(data.error || 'unknown').substring(0, 200)}`);
        }
      } catch (e) { /* ignore transient poll errors */ }
    }, 1500);
    return () => clearInterval(interval);
  }, [asyncJobId, asyncJobStatus, asyncRunId, fetchRuns, fetchRunDetails]);

  const openGroupFilter = (runId) => {
    setGroupFilterRunId(runId);
    setSelectedGroupIds([]);
    // Capture already-processed groups so the picker can dim them.
    const run = runs.find(r => r.id === runId);
    setAlreadyProcessedGroupIds(run?.processed_ctc_group_ids || []);
    setShowGroupPicker(true);
  };

  const handleRunAction = async (runId, action, remarks = '') => {
    try {
      await axios.post(`${API_URL}/payroll/runs/${runId}/action`, { action, remarks }, { withCredentials: true });
      fetchRuns();
      if (selectedRun?.id === runId) {
        const { data } = await axios.get(`${API_URL}/payroll/runs/${runId}`, { withCredentials: true });
        setSelectedRun(data);
      }
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleGeneratePayslips = async (runId) => {
    try {
      const { data } = await axios.post(`${API_URL}/payroll/runs/${runId}/generate-payslips`, {}, { withCredentials: true });
      alert(data.message);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  // Stage 73.20el — Release Payslip / Release Tax Sheet (independent).
  // Both endpoints are idempotent and duplicate-safe. Confirm before firing
  // — release is a customer-facing action that surfaces the document in the
  // Employee Portal, so a small guardrail dialog is worth the extra tap.
  //
  // When the run is in ``approved`` state and the caller triggers Release
  // Payslip, we route through the workflow ``/action`` endpoint so the run
  // ALSO transitions ``approved → released`` (which is the state needed to
  // enable a subsequent Lock). Post-transition, or for a run that was
  // already released via the workflow, we fall back to the dedicated
  // ``/release-payslip`` endpoint for idempotency + selective-employee
  // support.
  const handleReleaseDoc = async (run, scope) => {
    const label = scope === 'payslip' ? 'Payslip' : 'Tax Sheet';
    if (!window.confirm(
      `Release ${label} for all employees in this payroll month?\n\n` +
      `This will make the ${label.toLowerCase()} visible in the Employee Portal.`,
    )) return;
    try {
      let responseMsg = '';
      const useWorkflow = scope === 'payslip' && run.status === 'approved';
      if (useWorkflow) {
        await axios.post(
          `${API_URL}/payroll/runs/${run.id}/action`,
          { action: 'release', remarks: 'Release Payslip via Monthly Payroll Run' },
          { withCredentials: true },
        );
        responseMsg = `Payslip released — run moved to Released.`;
      } else {
        const url = `${API_URL}/payroll/runs/${run.id}/release-${scope === 'payslip' ? 'payslip' : 'tax-sheet'}`;
        const { data } = await axios.post(url, {}, { withCredentials: true });
        if (data.already_released) {
          toast.info(`${label} was already released for this run.`);
          return;
        }
        responseMsg = `${label} released for ${data.released_count} employee(s).`;
      }
      toast.success(responseMsg);
      fetchRuns();
      if (selectedRun?.id === run.id) {
        const { data: refreshed } = await axios.get(`${API_URL}/payroll/runs/${run.id}`, { withCredentials: true });
        setSelectedRun(refreshed);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to release ${label}`);
    }
  };

  const statusColors = {
    draft: 'bg-slate-100 text-slate-600', preview: 'bg-amber-100 text-amber-700',
    frozen: 'bg-cyan-100 text-cyan-700', pending_approval: 'bg-orange-100 text-orange-700',
    approved: 'bg-green-100 text-green-700', released: 'bg-indigo-100 text-indigo-700',
    locked: 'bg-slate-200 text-slate-700',
  };

  const getActionButtons = (run) => {
    const buttons = [];
    const processedCount = (run.processed_ctc_group_ids || []).length;
    const hasFilter = run.ctc_group_filter && run.ctc_group_filter.length > 0;
    const groupBtnLabel = processedCount > 0 ? 'Process Next Batch' : 'Process by Group';
    if (run.status === 'draft') {
      buttons.push({ label: 'Process All', action: () => handleProcessRun(run.id), icon: Play, color: 'bg-[#0052FF]' });
      buttons.push({ label: 'Process Async', action: () => handleProcessRunAsync(run.id), icon: Loader2, color: 'bg-emerald-700' });
      buttons.push({ label: groupBtnLabel, action: () => openGroupFilter(run.id), icon: Layers, color: 'bg-violet-600' });
    }
    if (run.status === 'preview') {
      // Rerun now goes through /process which auto-reverts the run to draft and pulls
      // in the latest monthly inputs in a single click.
      buttons.push({ label: 'Rerun All', action: () => handleProcessRun(run.id), icon: RotateCcw, color: 'bg-amber-600' });
      buttons.push({ label: 'Rerun Async', action: () => handleProcessRunAsync(run.id), icon: Loader2, color: 'bg-emerald-700' });
      buttons.push({ label: 'Selective Rerun', action: () => navigate(`/payroll/rerun/${run.id}`), icon: RotateCcw, color: 'bg-purple-600' });
      buttons.push({ label: groupBtnLabel, action: () => openGroupFilter(run.id), icon: Layers, color: 'bg-violet-600' });
      buttons.push({ label: 'Freeze', action: () => handleRunAction(run.id, 'freeze'), icon: Lock, color: 'bg-cyan-600' });
    }
    if (run.status === 'frozen') {
      buttons.push({ label: 'Rerun All', action: () => handleProcessRun(run.id), icon: RotateCcw, color: 'bg-amber-600' });
      buttons.push({ label: 'Selective Rerun', action: () => navigate(`/payroll/rerun/${run.id}`), icon: RotateCcw, color: 'bg-purple-600' });
      buttons.push({ label: groupBtnLabel, action: () => openGroupFilter(run.id), icon: Layers, color: 'bg-violet-600' });
      buttons.push({ label: 'Submit for Approval', action: () => handleRunAction(run.id, 'submit_for_approval'), icon: Send, color: 'bg-orange-600' });
    }
    // Suppress unused-var warning for hasFilter — kept for future progress UI.
    void hasFilter;
    if (run.status === 'pending_approval') {
      buttons.push({ label: 'Approve', action: () => handleRunAction(run.id, 'approve'), icon: CheckCircle, color: 'bg-green-600' });
      buttons.push({ label: 'Reject', action: () => handleRunAction(run.id, 'reject'), icon: RotateCcw, color: 'bg-red-600' });
    }
    if (run.status === 'approved') {
      // Stage 73.20el — The workflow "Release" button (approved→released
      // transition) is now surfaced as "Release Payslip" below (with the
      // "Release Tax Sheet" sibling right next to it). The old unified
      // Release button is intentionally removed to avoid confusion.
      buttons.push({ label: 'Payslips', action: () => handleGeneratePayslips(run.id), icon: Eye, color: 'bg-emerald-600' });
    }
    // Stage 73.20el — Release Payslip / Release Tax Sheet (independent).
    // Shown on any run that has been approved or beyond so the admin can
    // release either document at any time — the two lifecycles no longer
    // depend on each other. Buttons disable themselves when the flag is
    // 100% released to prevent duplicate releases (idempotent on the
    // backend as well; this is a UX belt-and-braces).
    const releaseCapable = ['approved', 'released', 'locked', 'paid', 'finalized', 'legacy_final'].includes(run.status);
    if (releaseCapable) {
      const total = run.employee_count || 0;
      const psCount = run.payslip_released_count || 0;
      const tsCount = run.tax_sheet_released_count || 0;
      const psDone = total > 0 && psCount >= total;
      const tsDone = total > 0 && tsCount >= total;
      buttons.push({
        label: psDone ? 'Payslip Released' : 'Release Payslip',
        action: () => handleReleaseDoc(run, 'payslip'),
        icon: Unlock,
        color: psDone ? 'bg-emerald-500 opacity-70' : 'bg-emerald-600',
        disabled: psDone,
      });
      buttons.push({
        label: tsDone ? 'Tax Sheet Released' : 'Release Tax Sheet',
        action: () => handleReleaseDoc(run, 'tax_sheet'),
        icon: FileSpreadsheet,
        color: tsDone ? 'bg-purple-500 opacity-70' : 'bg-purple-600',
        disabled: tsDone,
      });
    }
    if (run.status === 'released') buttons.push({ label: 'Lock', action: () => handleRunAction(run.id, 'lock'), icon: Lock, color: 'bg-slate-600' });
    // Stage 73.20dc — Delete Payroll Run.
    buttons.push({ label: 'Delete', action: () => { setDeleteRunModal(run); setDeleteConfirmText(''); }, icon: Trash2, color: 'bg-red-600' });
    return buttons;
  };

  return (
    <MainLayout>
      <div data-testid="payroll-run-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Monthly Payroll Run</h1>
            <p className="text-sm text-slate-500 mt-1">Process, preview, and manage payroll runs</p>
          </div>
          <div className="flex items-center space-x-3">
            {isSuperAdmin && (
              <button
                data-testid="global-view-toggle"
                onClick={() => setGlobalView(!globalView)}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${globalView ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50'}`}
                title="View payroll across all tenants"
              >
                <Globe className="w-4 h-4" /><span>{globalView ? 'Global View: ON' : 'Global View'}</span>
              </button>
            )}
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} disabled={globalView} className="border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50 disabled:bg-slate-50">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="new-run-btn" onClick={() => setShowNewRunModal(true)} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] transition-colors">
              <Play className="w-4 h-4" /><span>New Run</span>
            </button>
            <button data-testid="payroll-export-btn" onClick={() => {
              if (runs.length === 0) { toast.error('No payroll runs to export'); return; }
              downloadCSV(runs.map(r => ({
                Run_Number: r.run_number, Type: r.run_type, Employees: r.total_employees, Gross_Pay: r.total_gross, Net_Pay: r.total_net, Status: r.status, Period: r.period_name
              })), 'payroll_runs.csv');
              toast.success('Payroll runs exported');
            }} className="flex items-center space-x-2 bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors">
              <Download className="w-4 h-4" /><span>Export</span>
            </button>
          </div>
        </div>

        {/* Runs List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-slate-800">Payroll Runs</h2>
              {filterPeriodLabel && (
                <span className="px-2 py-0.5 bg-violet-100 text-violet-800 rounded text-xs font-semibold"
                  data-testid="active-period-filter-pill">
                  Period: {filterPeriodLabel} ({filteredRuns.length} of {runs.length})
                </span>
              )}
              {runGroupFilter && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold"
                  data-testid="active-group-filter-pill">
                  Group: {ctcGroups.find(g => g.id === runGroupFilter)?.name || runGroupFilter}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500">Period:</label>
                <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}
                  data-testid="period-filter"
                  className="h-8 px-2 text-xs border border-slate-300 rounded bg-white">
                  <option value="">{`All periods (${runs.length})`}</option>
                  {periods.map((p) => {
                    const cnt = runs.filter((r) => String(r.period_id || '') === String(p.id)).length;
                    const label = `${p.period_name || p.period}${cnt > 0 ? ` (${cnt})` : ''}`;
                    return <option key={p.id} value={p.id}>{label}</option>;
                  })}
                </select>
                {periodFilter && (
                  <button onClick={() => setPeriodFilter('')}
                    data-testid="clear-period-filter"
                    className="text-[11px] text-slate-500 hover:underline">clear</button>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500 font-medium">Group:</label>
                <select
                  value={runGroupFilter}
                  onChange={(e) => setRunGroupFilter(e.target.value)}
                  data-testid="group-filter"
                  className="h-8 px-2 text-xs border border-slate-300 rounded bg-white font-medium text-slate-800"
                >
                  <option value="">All Groups</option>
                  {ctcGroups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name || g.code}</option>
                  ))}
                </select>
                {runGroupFilter && (
                  <button onClick={() => setRunGroupFilter('')}
                    data-testid="clear-group-filter"
                    className="text-[11px] text-slate-500 hover:underline">clear</button>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Run #</th>
                <th className="px-4 py-3 text-left font-medium">Period</th>
                <th className="px-4 py-3 text-left font-medium">Group</th>
                {globalView && <th className="px-4 py-3 text-left font-medium">Company</th>}
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium">Employees</th>
                <th className="px-4 py-3 text-right font-medium">Total Gross</th>
                <th className="px-4 py-3 text-right font-medium">Total Net</th>
                <th className="px-4 py-3 text-left font-medium">Exceptions</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRuns.map((r) => (
                  <tr key={r.id} data-testid={`run-row-${r.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setSelectedRun(r); fetchRunDetails(r.id); setActiveView('details'); }}>
                    <td className="px-4 py-3 font-medium text-slate-800">#{r.run_number}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium">{r.period_name || r.period || '—'}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-semibold">
                        {r.group_name || (Array.isArray(r.ctc_group_filter) && r.ctc_group_filter.length > 0 ? (ctcGroups.find(g => r.ctc_group_filter.includes(g.id))?.name || 'Filtered Group') : 'All Groups')}
                      </span>
                    </td>
                    {globalView && <td className="px-4 py-3 text-slate-600">{r.company_name || r.company_id}</td>}
                    <td className="px-4 py-3 text-slate-600 capitalize">{r.run_type}</td>
                    <td className="px-4 py-3 text-slate-600">{r.total_employees || 0}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{(r.total_gross || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{(r.total_net || 0).toLocaleString()}</td>
                    <td className="px-4 py-3">{r.exception_count > 0 ? <span className="text-red-600 font-medium">{r.exception_count}</span> : <span className="text-slate-400">0</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span data-testid={`run-status-${r.id}`} className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[r.status] || 'bg-slate-100'}`}>{r.status?.replace('_', ' ')}</span>
                        {/* Stage 73.20el — Independent release chips for Payslip / Tax Sheet. */}
                        {(() => {
                          const total = r.total_employees || r.employee_count || 0;
                          if (total === 0) return null;
                          const psCount = r.payslip_released_count || 0;
                          const tsCount = r.tax_sheet_released_count || 0;
                          const psDone = psCount >= total;
                          const tsDone = tsCount >= total;
                          const chip = (label, done, count, testId) => (
                            <span
                              data-testid={testId}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                                done
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : count > 0
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-slate-50 text-slate-500 border-slate-200'
                              }`}
                              title={`${label}: ${count}/${total} released`}
                            >
                              {label}: {done ? '✓' : count > 0 ? `${count}/${total}` : 'Pending'}
                            </span>
                          );
                          return (
                            <>
                              {chip('Payslip', psDone, psCount, `payslip-release-chip-${r.id}`)}
                              {chip('Tax Sheet', tsDone, tsCount, `tax-sheet-release-chip-${r.id}`)}
                            </>
                          );
                        })()}
                        {(r.processed_ctc_group_ids || []).length > 0 && (
                          <span data-testid={`batch-progress-${r.id}`} className="inline-block px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded text-[10px] font-semibold" title="CTC groups processed in this run so far">
                            {(r.processed_ctc_group_ids || []).length} grp{(r.processed_ctc_group_ids || []).length === 1 ? '' : 's'} done
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-1">
                        {getActionButtons(r).slice(0, 3).map((btn, i) => {
                          const Icon = btn.icon;
                          return <button key={i} data-testid={`run-action-${btn.label.toLowerCase().replace(/\s/g, '-')}-${r.id}`} onClick={btn.action} disabled={processing || btn.disabled} className={`${btn.color} text-white px-2 py-1 rounded text-xs font-medium hover:opacity-90 flex items-center space-x-1 disabled:cursor-not-allowed`}><Icon className="w-3 h-3" /><span>{btn.label}</span></button>;
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRuns.length === 0 && <tr><td colSpan={globalView ? 10 : 9} className="px-4 py-8 text-center text-slate-400">{periodFilter ? `No runs for the selected period — try clearing the filter to see all ${runs.length} runs.` : 'No payroll runs yet'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Run Details — Bifurcated Payroll Register */}
        {selectedRun && activeView === 'details' && (
          <>
            {/* Phase 2 — HR Bench Pre-payroll validation widget */}
            <PrePayrollValidation
              companyId={selectedRun.company_id}
              periodYear={selectedRun.statutory_year || selectedRun.year}
              periodMonth={selectedRun.statutory_month || selectedRun.month}
              runStatus={selectedRun.status}
            />
            <PayrollRegister
              run={selectedRun}
              details={runDetails}
              statusColors={statusColors}
              onClose={() => setActiveView('runs')}
              actionButtons={getActionButtons(selectedRun)}
              processing={processing}
              staleInfo={staleInfo}
              miDiag={miDiag}
              onReprocess={() => handleReprocess(selectedRun.id)}
              ctcGroups={ctcGroups}
            />
          </>
        )}

        {/* New Run Modal */}
        {showNewRunModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowNewRunModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">New Payroll Run</h3>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4 text-xs text-indigo-900 flex gap-2">
                <Eye className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <strong>Monthly inputs auto-merge.</strong> Any OT / incentive / arrears / reimbursement / other earnings / deductions you entered for this period in <strong>Controls → Monthly Inputs</strong> (any status except <em>rejected</em>) are picked up automatically when the run is processed. Rows marked <strong>Hold Salary</strong> will show net = 0 with status <code>on_hold</code>.
                </div>
              </div>
              <form onSubmit={handleCreateRun} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Payroll Period</label>
                  <select data-testid="run-period-select" value={newRunForm.period_id} onChange={(e) => setNewRunForm({ ...newRunForm, period_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required>
                    <option value="">Select Period</option>{periods.filter(p => p.status !== 'locked').map((p) => <option key={p.id} value={p.id}>{`${p.period_name} (${p.status})`}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Group Scope</label>
                  <select
                    data-testid="run-group-select"
                    value={newRunForm.group_id || ''}
                    onChange={(e) => {
                      const gid = e.target.value;
                      const grp = ctcGroups.find(g => g.id === gid);
                      setNewRunForm({
                        ...newRunForm,
                        group_id: gid,
                        group_name: grp ? (grp.name || grp.code) : 'All Groups'
                      });
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium text-slate-800"
                  >
                    <option value="">All Groups (Entire Company)</option>
                    {ctcGroups.map((g) => (
                      <option key={g.id} value={g.id}>{`${g.name || g.code} (${g.employee_count || 0} employees)`}</option>
                    ))}
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Run Type</label>
                  <select value={newRunForm.run_type} onChange={(e) => setNewRunForm({ ...newRunForm, run_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option value="regular">Regular</option><option value="supplementary">Supplementary</option><option value="off_cycle">Off-Cycle</option><option value="arrear">Arrear</option><option value="bonus">Bonus</option></select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <input value={newRunForm.description} onChange={(e) => setNewRunForm({ ...newRunForm, description: e.target.value })} placeholder="Optional" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowNewRunModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="create-run-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Create Run</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk-by-Group Picker Modal */}
        {showGroupPicker && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowGroupPicker(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="group-picker-modal">
              <div className="p-5 border-b border-violet-200 bg-violet-50">
                <h2 className="font-bold text-violet-800 flex items-center gap-2">
                  <Layers size={20} /> Select CTC Groups to Process
                </h2>
                <p className="text-xs text-violet-700 mt-1">
                  Pick parent group, sub-group, or individual structures. Only employees mapped to selected CTC groups will be processed.
                </p>
              </div>
              <div className="p-5">
                <CTCGroupTreePicker
                  companyId={selectedCompany}
                  value={selectedGroupIds}
                  onChange={setSelectedGroupIds}
                  processedGroupIds={alreadyProcessedGroupIds}
                />
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                <button onClick={() => setShowGroupPicker(false)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm">Cancel</button>
                <button
                  onClick={() => handleProcessRun(groupFilterRunId, selectedGroupIds)}
                  disabled={processing || selectedGroupIds.length === 0}
                  data-testid="run-payroll-by-group"
                  className="px-5 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1">
                  {processing ? <RotateCcw size={14} className="animate-spin" /> : <Play size={14} />}
                  Run Payroll for {selectedGroupIds.length} Selected Group{selectedGroupIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ====== Async Job Progress Modal ====== */}
        {asyncJob && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
               data-testid="async-job-modal"
               onClick={() => { if (['completed', 'failed'].includes(asyncJob.status)) setAsyncJob(null); }}>
            <div className="bg-white rounded-xl border-2 border-emerald-200 w-full max-w-md p-5 space-y-4 shadow-2xl"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm text-emerald-800 flex items-center gap-2">
                  {asyncJob.status === 'completed'
                    ? <CheckCircle className="w-4 h-4 text-emerald-600" />
                    : asyncJob.status === 'failed'
                      ? <X className="w-4 h-4 text-red-600" />
                      : <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />}
                  Async Payroll Job
                </h4>
                <button onClick={() => setAsyncJob(null)}
                  disabled={!['completed', 'failed'].includes(asyncJob.status)}
                  title={['completed', 'failed'].includes(asyncJob.status) ? 'Close' : 'Cannot close while running'}
                  className="text-slate-400 hover:text-slate-600 disabled:opacity-40">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="text-xs text-slate-600 space-y-1">
                <div>Status: <span className={`font-medium ${
                  asyncJob.status === 'completed' ? 'text-emerald-700'
                    : asyncJob.status === 'failed' ? 'text-red-700'
                      : 'text-amber-700'
                }`}>{asyncJob.status}</span></div>
                <div>Step: <span className="font-mono text-slate-700">{asyncJob.current_step || '—'}</span></div>
                <div>Job ID: <span className="font-mono text-[10px] text-slate-500">{asyncJob.job_id}</span></div>
              </div>

              {/* Progress bar */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">Progress</span>
                  <span className="font-mono text-slate-800" data-testid="async-job-progress">
                    {asyncJob.processed} / {asyncJob.total || '?'}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${
                    asyncJob.status === 'completed' ? 'bg-emerald-500'
                      : asyncJob.status === 'failed' ? 'bg-red-500'
                        : 'bg-blue-500'
                  }`}
                  style={{ width: asyncJob.total
                    ? `${Math.min(100, (asyncJob.processed / asyncJob.total) * 100)}%`
                    : (asyncJob.status === 'running' ? '50%' : '0%') }}
                  />
                </div>
              </div>

              {asyncJob.status === 'completed' && asyncJob.result && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs space-y-1" data-testid="async-job-result">
                  <div className="font-semibold text-emerald-800">Run complete</div>
                  <div>Employees processed: <span className="font-mono">{asyncJob.result.total_employees}</span></div>
                  <div>Exceptions: <span className="font-mono">{asyncJob.result.exception_count || 0}</span></div>
                  {asyncJob.result.total_gross !== undefined && (
                    <>
                      <div>Total gross: ₹<span className="font-mono">{fmt(asyncJob.result.total_gross)}</span></div>
                      <div>Total net: ₹<span className="font-mono">{fmt(asyncJob.result.total_net)}</span></div>
                    </>
                  )}
                </div>
              )}

              {asyncJob.status === 'failed' && asyncJob.error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs space-y-1" data-testid="async-job-error">
                  <div className="font-semibold text-red-800">Run failed</div>
                  <div className="font-mono text-red-700 text-[10px] whitespace-pre-wrap break-words">
                    {asyncJob.error.substring(0, 600)}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button onClick={() => setAsyncJob(null)}
                  disabled={!['completed', 'failed'].includes(asyncJob.status)}
                  className="px-4 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                  {['completed', 'failed'].includes(asyncJob.status) ? 'Close' : 'Running…'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage 73.20dc — Delete Payroll Run confirmation modal */}
        {deleteRunModal && (() => {
          const isDraft = deleteRunModal.status === 'draft';
          const isReleased = ['released', 'locked', 'paid', 'finalized'].includes(deleteRunModal.status) || 
                             (deleteRunModal.payslip_released_count || 0) > 0 || 
                             (deleteRunModal.tax_sheet_released_count || 0) > 0;
          
          if (isReleased) {
            return (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-run-modal">
                <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-200">
                  <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h3 className="text-base font-semibold text-amber-900">Delete Payroll Run</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <p className="text-sm text-slate-700 font-medium">
                      This payroll has already been released. It cannot be deleted.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                    <button
                      onClick={() => setDeleteRunModal(null)}
                      className="px-4 py-1.5 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-100 bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setDeleteRunModal(null);
                        toast.success("Reverse Run creation initiated.");
                      }}
                      className="px-4 py-1.5 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md text-sm font-medium"
                    >
                      Create Reverse Run
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          if (isDraft) {
            return (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-run-modal">
                <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-200">
                  <div className="bg-red-50 border-b border-red-200 px-5 py-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <h3 className="text-base font-semibold text-red-900">Delete Draft Payroll Run</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <p className="text-sm text-slate-700">
                      Are you sure you want to permanently delete draft payroll run <b>{deleteRunModal.run_number}</b>? This will completely wipe all its calculations and temporary files.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                    <button
                      onClick={() => setDeleteRunModal(null)}
                      disabled={deleting}
                      className="px-4 py-1.5 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-100 bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteRun}
                      disabled={deleting}
                      data-testid="delete-run-confirm"
                      className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                      {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          // Processed or Finalized run but NOT released
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-run-modal">
              <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
                <div className="bg-red-50 border-b border-red-200 px-5 py-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h3 className="text-base font-semibold text-red-900">Delete Payroll Run</h3>
                </div>
                <div className="p-5 space-y-4">
                  <p className="text-sm text-slate-800 font-semibold">
                    This will permanently remove:
                  </p>
                  <ul className="text-sm text-slate-600 list-disc list-inside space-y-1 pl-1">
                    <li>Payroll calculations</li>
                    <li>Employee payroll records</li>
                    <li>Tax computations</li>
                    <li>Payslip data</li>
                    <li>Approval history</li>
                    <li>Register</li>
                    <li>Exceptions</li>
                  </ul>
                  <p className="text-xs text-red-600 font-semibold bg-red-50 p-2 rounded border border-red-100">
                    This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                  <button
                    onClick={() => setDeleteRunModal(null)}
                    disabled={deleting}
                    className="px-4 py-1.5 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-100 bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteRun}
                    disabled={deleting}
                    data-testid="delete-run-confirm"
                    className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </MainLayout>
  );
};

export default PayrollRun;

/**
 * Master column specifications for Employee Details in Salary Register
 */
const EMP_DETAIL_COLUMNS = [
  // Basic Information
  { id: 'emp_code', label: 'Employee Code', group: 'Basic Information', default: true },
  { id: 'emp_id', label: 'Employee ID', group: 'Basic Information', default: false },
  { id: 'emp_name', label: 'Employee Name', group: 'Basic Information', default: true },
  { id: 'emp_status', label: 'Employee Status', group: 'Basic Information', default: true },
  { id: 'emp_type', label: 'Employment Type', group: 'Basic Information', default: true },
  { id: 'gender', label: 'Gender', group: 'Basic Information', default: false },
  { id: 'dob', label: 'Date of Birth (DOB)', group: 'Basic Information', default: false },
  { id: 'age', label: 'Age', group: 'Basic Information', default: false },
  { id: 'marital_status', label: 'Marital Status', group: 'Basic Information', default: false },
  { id: 'blood_group', label: 'Blood Group', group: 'Basic Information', default: false },
  { id: 'mobile', label: 'Mobile Number', group: 'Basic Information', default: false },
  { id: 'email', label: 'Email ID', group: 'Basic Information', default: false },

  // Employment Information
  { id: 'doj', label: 'Date of Joining (DOJ)', group: 'Employment Information', default: true },
  { id: 'doc', label: 'Date of Confirmation', group: 'Employment Information', default: false },
  { id: 'doe', label: 'Date of Exit', group: 'Employment Information', default: false },
  { id: 'service_years', label: 'Years of Service', group: 'Employment Information', default: false },
  { id: 'department', label: 'Department', group: 'Employment Information', default: true },
  { id: 'designation', label: 'Designation', group: 'Employment Information', default: true },
  { id: 'grade', label: 'Grade', group: 'Employment Information', default: false },
  { id: 'band', label: 'Band', group: 'Employment Information', default: false },
  { id: 'category', label: 'Category', group: 'Employment Information', default: false },
  { id: 'cost_center', label: 'Cost Center', group: 'Employment Information', default: false },
  { id: 'business_unit', label: 'Business Unit', group: 'Employment Information', default: false },
  { id: 'location', label: 'Location / Branch', group: 'Employment Information', default: true },
  { id: 'pt_state', label: 'PT State', group: 'Employment Information', default: true },
  { id: 'reporting_manager', label: 'Reporting Manager', group: 'Employment Information', default: false },
  { id: 'hrbp', label: 'HR Business Partner', group: 'Employment Information', default: false },
  { id: 'shift', label: 'Shift', group: 'Employment Information', default: false },

  // Payroll Information
  { id: 'payroll_group', label: 'Payroll Group', group: 'Payroll Information', default: false },
  { id: 'salary_structure', label: 'Salary Structure / CTC Group', group: 'Payroll Information', default: true },
  { id: 'pay_grade', label: 'Pay Grade', group: 'Payroll Information', default: false },
  { id: 'wage_type', label: 'Wage Type', group: 'Payroll Information', default: false },
  { id: 'salary_mode', label: 'Salary Mode', group: 'Payroll Information', default: false },
  { id: 'monthly_ctc', label: 'Monthly CTC', group: 'Payroll Information', default: true },

  // Bank Details
  { id: 'bank_name', label: 'Bank Name', group: 'Bank Details', default: true },
  { id: 'account_holder', label: 'Account Holder Name', group: 'Bank Details', default: false },
  { id: 'account_no', label: 'Account Number', group: 'Bank Details', default: true },
  { id: 'ifsc_code', label: 'IFSC Code', group: 'Bank Details', default: true },
  { id: 'branch_name', label: 'Bank Branch', group: 'Bank Details', default: false },
  { id: 'payment_method', label: 'Payment Method', group: 'Bank Details', default: false },

  // Statutory Details
  { id: 'pf_eligible', label: 'PF Eligible', group: 'Statutory Details', default: true },
  { id: 'pf_number', label: 'PF Number', group: 'Statutory Details', default: false },
  { id: 'uan', label: 'UAN', group: 'Statutory Details', default: false },
  { id: 'esi_eligible', label: 'ESI Eligible', group: 'Statutory Details', default: true },
  { id: 'esi_number', label: 'ESI Number', group: 'Statutory Details', default: false },
  { id: 'lwf_state', label: 'LWF State', group: 'Statutory Details', default: false },
  { id: 'tds_regime', label: 'TDS Regime', group: 'Statutory Details', default: false },
  { id: 'pan', label: 'PAN', group: 'Statutory Details', default: false },
  { id: 'aadhaar', label: 'Aadhaar', group: 'Statutory Details', default: false },

  // Attendance Information
  { id: 'working_days', label: 'Working Days', group: 'Attendance Information', default: false },
  { id: 'present_days', label: 'Present Days', group: 'Attendance Information', default: false },
  { id: 'paid_days', label: 'Paid Days', group: 'Attendance Information', default: false },
  { id: 'lop_days', label: 'LOP Days', group: 'Attendance Information', default: false },

  // Payroll Status
  { id: 'payroll_source', label: 'Payroll Source', group: 'Payroll Status', default: false },
  { id: 'payroll_status', label: 'Payroll Status', group: 'Payroll Status', default: false },
];

const DEFAULT_SELECTED_COLS = EMP_DETAIL_COLUMNS.filter(c => c.default).map(c => c.id);

const formatDateVal = (val) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return String(val);
  }
};

const calcAgeVal = (dob) => {
  if (!dob) return '—';
  try {
    const bday = new Date(dob);
    if (isNaN(bday.getTime())) return '—';
    const ageDifMs = Date.now() - bday.getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    return `${age} yrs`;
  } catch (e) {
    return '—';
  }
};

const calcYearsServiceVal = (doj) => {
  if (!doj) return '—';
  try {
    const jday = new Date(doj);
    if (isNaN(jday.getTime())) return '—';
    const diffMs = Date.now() - jday.getTime();
    if (diffMs < 0) return '0 yrs';
    const years = (diffMs / (365.25 * 24 * 60 * 60 * 1000)).toFixed(1);
    return `${years} yrs`;
  } catch (e) {
    return '—';
  }
};

const maskAadhaarVal = (val) => {
  if (!val) return '—';
  const clean = String(val).replace(/\D/g, '');
  if (clean.length < 4) return val;
  return `XXXX XXXX ${clean.slice(-4)}`;
};

const maskAccountNoVal = (val) => {
  if (!val) return '—';
  const str = String(val).trim();
  if (str.length <= 4) return str;
  return `${'X'.repeat(Math.max(4, str.length - 4))}${str.slice(-4)}`;
};

const maskUANVal = (val) => {
  if (!val) return '—';
  const str = String(val).trim();
  if (str.length <= 4) return str;
  return `${'X'.repeat(Math.max(4, str.length - 4))}${str.slice(-4)}`;
};

const maskPANVal = (val) => {
  if (!val) return '—';
  const str = String(val).trim().toUpperCase();
  if (str.length !== 10) return str;
  return `XXXXX${str.slice(5, 9)}X`;
};

const maskPFVal = (val) => {
  if (!val) return '—';
  const str = String(val).trim();
  if (str.length <= 5) return str;
  return `${str.slice(0, 3)}/***/${str.slice(-4)}`;
};

/**
 * Bifurcated Payroll Register — collects all unique earning / deduction heads
 * from runDetails and renders dynamic columns per head. Includes CSV export
 * of the full bifurcated register so users can reconcile salary split.
 */
const PayrollRegister = ({ run, details, statusColors, onClose, actionButtons, processing, staleInfo, miDiag, onReprocess, ctcGroups = [] }) => {
  const [groupFilter, setGroupFilter] = useState(null); // {code, name, id} or string or null
  const [employeeMasters, setEmployeeMasters] = useState({});
  const [registerSearch, setRegisterSearch] = useState('');
  const [maskSensitive, setMaskSensitive] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [colSearchQuery, setColSearchQuery] = useState('');
  const [selectedColumns, setSelectedColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('salary_register_selected_columns_v4');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const unique = Array.from(new Set(parsed));
          if (!unique.includes('pt_state')) {
            const locIdx = unique.indexOf('location');
            if (locIdx !== -1) {
              unique.splice(locIdx + 1, 0, 'pt_state');
            } else {
              unique.push('pt_state');
            }
          }
          return unique;
        }
      }
      return DEFAULT_SELECTED_COLS;
    } catch (e) {
      return DEFAULT_SELECTED_COLS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('salary_register_selected_columns_v4', JSON.stringify(selectedColumns));
    } catch (e) {}
  }, [selectedColumns]);

  useEffect(() => {
    let isMounted = true;
    const fetchEmployeeMasters = async () => {
      if (!run?.company_id) return;
      try {
        const [empRes, statRes] = await Promise.all([
          axios.get(`${API_URL}/employees?company_id=${run.company_id}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/statutory/employee-info?company_id=${run.company_id}`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (!isMounted) return;
        const emps = Array.isArray(empRes.data) ? empRes.data : [];
        const stats = Array.isArray(statRes.data) ? statRes.data : [];
        const statMap = {};
        stats.forEach(s => {
          if (s.employee_id) statMap[s.employee_id] = s;
        });

        const masterMap = {};
        emps.forEach(e => {
          const id = e.id || e._id;
          const code = e.employment_details?.employee_code;
          const merged = { ...e, statutory_info: statMap[id] || {} };
          if (id) masterMap[id] = merged;
          if (code) masterMap[code] = merged;
        });
        setEmployeeMasters(masterMap);
      } catch (e) {
        console.error('Error fetching employee masters:', e);
      }
    };
    fetchEmployeeMasters();
    return () => { isMounted = false; };
  }, [run?.company_id]);

  const filteredDetails = useMemo(() => {
    const list = Array.isArray(details) ? details : [];
    let result = list;
    if (groupFilter) {
      const gId = typeof groupFilter === 'object' ? groupFilter?.id : groupFilter;
      const gCode = typeof groupFilter === 'object' ? groupFilter?.code : groupFilter;
      result = result.filter((d) =>
        (gId && String(d.ctc_group_id) === String(gId)) ||
        (gCode && String(d.ctc_group_code).toLowerCase() === String(gCode).toLowerCase())
      );
    }
    if (registerSearch.trim()) {
      const q = registerSearch.trim().toLowerCase();
      result = result.filter(d => {
        const emp = employeeMasters[d.employee_id] || employeeMasters[d.employee_code] || {};
        const code = (d.employee_code || '').toLowerCase();
        const name = (d.employee_name || '').toLowerCase();
        const dept = (emp.employment_details?.department || '').toLowerCase();
        const desig = (emp.employment_details?.designation || '').toLowerCase();
        const loc = (emp.employment_details?.location || '').toLowerCase();
        return code.includes(q) || name.includes(q) || dept.includes(q) || desig.includes(q) || loc.includes(q);
      });
    }
    return result;
  }, [details, groupFilter, registerSearch, employeeMasters]);

  const visibleEmpDetailCols = useMemo(() => {
    return EMP_DETAIL_COLUMNS.filter(c => selectedColumns.includes(c.id));
  }, [selectedColumns]);

  const getColValue = (colId, d, emp) => {
    const pd = emp?.personal_details || {};
    const ed = emp?.employment_details || {};
    const bank = emp?.bank_details || {};
    const kyc = emp?.kyc_statutory_details || {};
    const stat = emp?.statutory_info || {};

    switch (colId) {
      case 'emp_code':
        return ed.employee_code || d.employee_code || (d.employee_id ? `id-${String(d.employee_id).slice(-6)}` : '—');
      case 'emp_id':
        return emp?.id || d.employee_id || '—';
      case 'emp_name':
        return pd.full_name || (pd.first_name ? `${pd.first_name} ${pd.last_name || ''}`.trim() : d.employee_name || '—');
      case 'emp_status':
        return ed.employment_status || d.status || 'Active';
      case 'emp_type':
        return ed.employment_type || ed.employment_type_name || 'Permanent';
      case 'gender':
        return pd.gender || '—';
      case 'dob':
        return formatDateVal(pd.date_of_birth);
      case 'age':
        return calcAgeVal(pd.date_of_birth);
      case 'marital_status':
        return pd.marital_status || '—';
      case 'blood_group':
        return pd.blood_group || '—';
      case 'mobile':
        return pd.mobile || '—';
      case 'email':
        return ed.work_email || pd.personal_email || pd.email || '—';
      case 'doj':
        return formatDateVal(ed.date_of_joining);
      case 'doc':
        return formatDateVal(ed.date_of_confirmation || ed.confirmation_date);
      case 'doe':
        return formatDateVal(ed.date_of_exit || ed.exit_date);
      case 'service_years':
        return calcYearsServiceVal(ed.date_of_joining);
      case 'department':
        return ed.department || ed.department_name || '—';
      case 'designation':
        return ed.designation || ed.designation_name || '—';
      case 'grade':
        return ed.grade || ed.grade_name || '—';
      case 'band':
        return ed.band || '—';
      case 'category':
        return ed.employee_category || ed.category_name || '—';
      case 'cost_center':
        return ed.cost_center || '—';
      case 'business_unit':
        return ed.business_unit || '—';
      case 'location':
        return ed.location || ed.work_location || ed.branch || '—';
      case 'reporting_manager':
        return ed.reporting_manager_name || ed.reporting_manager || '—';
      case 'hrbp':
        return ed.hrbp_name || ed.hrbp || '—';
      case 'shift':
        return ed.shift || ed.shift_name || '—';
      case 'payroll_group':
        return ed.payroll_group || '—';
      case 'salary_structure':
        return d.ctc_group_code || ed.ctc_group_code || 'GROUP';
      case 'pay_grade':
        return ed.pay_grade || '—';
      case 'wage_type':
        return ed.wage_type || 'Monthly';
      case 'salary_mode':
        return ed.salary_mode || bank.payment_mode || 'Bank Transfer';
      case 'monthly_ctc':
        return ed.ctc_annual ? fmt(Math.round(ed.ctc_annual / 12)) : (d.gross_fixed ? fmt(d.gross_fixed) : '—');
      case 'bank_name':
        return bank.bank_name || '—';
      case 'account_holder':
        return bank.account_holder_name || pd.full_name || d.employee_name || '—';
      case 'account_no':
        const acc = bank.account_number || bank.account_no || bank.bank_account_number || emp.account_number || emp.bank_account_number;
        return maskSensitive ? maskAccountNoVal(acc) : (acc || '—');
      case 'ifsc_code':
        return bank.ifsc_code || '—';
      case 'branch_name':
        return bank.branch_name || bank.bank_branch || '—';
      case 'payment_method':
        return bank.payment_method || bank.payment_mode || 'Bank Transfer';
      case 'pf_eligible':
        return stat.pf?.is_eligible !== undefined ? (stat.pf.is_eligible ? 'Yes' : 'No') : 'Yes';
      case 'pf_number':
        const pfNum = stat.pf?.pf_number || kyc.pf_number;
        return maskSensitive ? maskPFVal(pfNum) : (pfNum || '—');
      case 'uan':
        const uanNum = stat.pf?.uan || kyc.uan_number;
        return maskSensitive ? maskUANVal(uanNum) : (uanNum || '—');
      case 'esi_eligible':
        return stat.esi?.is_eligible !== undefined ? (stat.esi.is_eligible ? 'Yes' : 'No') : 'Yes';
      case 'esi_number':
        const esiNum = stat.esi?.esic_number || kyc.esic_number;
        return maskSensitive ? maskUANVal(esiNum) : (esiNum || '—');
      case 'pt_state': {
        const val =
          d.ptState ||
          d.pt_state ||
          d.work_state ||
          ed.work_state ||
          ed.pt_state ||
          ed.state ||
          stat.pt_state ||
          stat.work_state ||
          stat.state ||
          emp.pt_state ||
          emp.ptState ||
          pd.state;
        return (val && String(val).trim()) ? String(val).trim() : 'Not Assigned';
      }
      case 'lwf_state':
        return stat.lwf_state || stat.pt_state || pd.state || '—';
      case 'tds_regime':
        return (stat.tds?.regime || 'new').toUpperCase();
      case 'pan':
        const panNum = stat.tds?.pan || kyc.pan_number;
        return maskSensitive ? maskPANVal(panNum) : (panNum || '—');
      case 'aadhaar':
        const aadhNum = kyc.aadhaar_number;
        return maskSensitive ? maskAadhaarVal(aadhNum) : (aadhNum || '—');
      case 'working_days':
        return d.base_days ?? d.total_days ?? '—';
      case 'present_days':
        return d.payable_days ?? '—';
      case 'paid_days':
        return d.payable_days ?? '—';
      case 'lop_days':
        return d.lop_days ?? 0;
      case 'payroll_source':
        return d.structure_source === 'employee_custom' ? 'CUSTOM' : 'REGULAR';
      case 'payroll_status':
        return d.status ? (d.hold_reason ? `${d.status} · ${d.hold_reason}` : d.status) : 'draft';
      default:
        return '—';
    }
  };

const DERIVED_OR_REFERENCE_CODES = new Set([
  'GROSS_MONTHLY', 'GROSS', 'GROSS_PAY', 'EARNED_GROSS', 'FIXED_GROSS', 'TOTAL_GROSS',
  'CTC_MONTHLY', 'CTC', 'MONTHLY_CTC', 'CTC_REF', 'CTC_REFERENCE', 'CTC_ANNUAL',
  'NET_PAY', 'NET', 'NET_SALARY', 'TOTAL_DEDUCTIONS', 'TOTAL_EMPLOYER_CONTRIBUTIONS', 'EMPLOYER_COST'
]);

const NON_MONETARY_CODES = new Set([
  'ATT_OT_HOURS', 'OT_HOURS', 'SYSTEM_ATT_OT_HOURS',
  'PRESENT_DAYS', 'PRESENT_DAYS_OVERRIDE', 'SYSTEM_PRESENT_DAYS',
  'SYSTEM_LOP_DAYS', 'LOP_DAYS', 'LOP_DAYS_OVERRIDE',
  'LEAVE_DAYS', 'HOLIDAYS', 'WEEKLY_OFF', 'WEEKLY_OFFS',
  'TOTAL_DAYS', 'MONTH_DAYS', 'BASE_DAYS', 'BASE_DAYS_OVERRIDE',
  'ADDITIONAL_DAYS', 'PAYABLE_DAYS', 'PAID_DAYS', 'DAYS',
  'HAS_INCREMENT', 'IS_NEW_JOINEE', 'IS_RESIGNEE', 'IS_UNDER_NOTICE',
]);

const isNonMonetaryField = (headOrCode) => {
  if (!headOrCode) return false;
  let code = '';
  if (typeof headOrCode === 'string') {
    code = headOrCode.trim().toUpperCase();
  } else {
    code = String(headOrCode.head_code || headOrCode.code || headOrCode.id || '').trim().toUpperCase();
  }
  if (code.startsWith('EARNED_')) code = code.slice(7);
  else if (code.startsWith('FIXED_')) code = code.slice(6);
  else if (code.startsWith('DED_')) code = code.slice(4);

  if (NON_MONETARY_CODES.has(code)) return true;
  if (code.endsWith('_DAYS') || code.endsWith('_HOURS') || code.endsWith('_DAY') || code.endsWith('_HOUR')) return true;
  if (code.startsWith('SYSTEM_') || code.startsWith('HAS_') || code.startsWith('IS_')) return true;
  return false;
};

const isDerivedOrReferenceHead = (head) => {
  if (!head) return false;
  if (isNonMonetaryField(head)) return true;
  if (typeof head === 'string') {
    const code = head.trim().toUpperCase();
    return DERIVED_OR_REFERENCE_CODES.has(code);
  }
  const code = String(head.head_code || head.code || '').trim().toUpperCase();
  const headType = String(head.head_type || head.type || head.category || '').trim().toLowerCase();
  if (['derived', 'ctc_reference', 'reference', 'summary', 'ctc_monthly'].includes(headType)) return true;
  if (head.is_derived === true || head.isDerived === true) return true;
  if (head.is_earning === false || head.isEarning === false) {
    if (!['deduction', 'employer_contribution'].includes(headType)) return true;
  }
  if (head.is_part_of_gross === false || head.includeInGross === false || head.include_in_gross === false) {
    if (!['deduction', 'employer_contribution'].includes(headType)) return true;
  }
  if (DERIVED_OR_REFERENCE_CODES.has(code)) return true;
  return false;
};

const isCtcReferenceHead = isDerivedOrReferenceHead;


  const { earningHeads, fixedEarningHeads, deductionHeads, employerHeads, nameByCode } = useMemo(() => {
    const eOrder = [], fxOrder = [], dOrder = [], erOrder = [];
    const eSeen = new Set(), fxSeen = new Set(), dSeen = new Set(), erSeen = new Set();
    const names = {};
    filteredDetails.forEach((d) => {
      (d.earnings || []).forEach((c) => {
        if (!c?.head_code) return;
        if (isCtcReferenceHead(c)) return;
        names[c.head_code] = c.head_name || c.head_code;
        if (!eSeen.has(c.head_code)) { eSeen.add(c.head_code); eOrder.push(c.head_code); }
        const isMI = c.source === 'monthly_input' || c.source === 'monthly_input_auto' || c.is_monthly_input === true;
        const isFixed = !isMI && (c.fixed_amount !== undefined ? Number(c.fixed_amount) > 0 : true);
        if (isFixed && !fxSeen.has(c.head_code)) {
          fxSeen.add(c.head_code);
          fxOrder.push(c.head_code);
        }
      });
      (d.deductions || []).forEach((c) => {
        if (!c?.head_code) return;
        names[c.head_code] = c.head_name || c.head_code;
        if (!dSeen.has(c.head_code)) { dSeen.add(c.head_code); dOrder.push(c.head_code); }
      });
      (d.employer_contributions || []).forEach((c) => {
        if (!c?.head_code) return;
        names[c.head_code] = c.head_name || c.head_code;
        if (!erSeen.has(c.head_code)) { erSeen.add(c.head_code); erOrder.push(c.head_code); }
      });
    });
    return { earningHeads: eOrder, fixedEarningHeads: fxOrder, deductionHeads: dOrder, employerHeads: erOrder, nameByCode: names };
  }, [filteredDetails]);

  const earnedFor = (rows, code) => {
    const hit = (rows || []).find((r) => r.head_code === code);
    return hit ? Number(hit.amount) || 0 : 0;
  };
  const fixedFor = (d, code) => {
    const hit = (d.earnings || []).find((r) => r.head_code === code);
    if (!hit) return 0;
    if (Object.prototype.hasOwnProperty.call(hit, 'fixed_amount') && hit.fixed_amount !== null) {
      return Number(hit.fixed_amount) || 0;
    }
    const total = Number(d.total_days) || 0;
    const payable = Number(d.payable_days) || 0;
    const amount = Number(hit.amount) || 0;
    if (payable > 0 && total > 0) return Math.round((amount * total) / payable * 100) / 100;
    return amount;
  };

  const totals = useMemo(() => {
    const t = {
      fixed: {}, earnings: {}, deductions: {}, employer: {},
      gross_fixed: 0, gross: 0, deduct: 0, net: 0, employer_total: 0, final_ctc: 0,
    };
    fixedEarningHeads.forEach((c) => { t.fixed[c] = 0; });
    earningHeads.forEach((c) => { t.earnings[c] = 0; });
    deductionHeads.forEach((c) => { t.deductions[c] = 0; });
    employerHeads.forEach((c) => { t.employer[c] = 0; });
    filteredDetails.forEach((d) => {
      fixedEarningHeads.forEach((c) => {
        t.fixed[c] += fixedFor(d, c);
      });
      earningHeads.forEach((c) => {
        t.earnings[c] += earnedFor(d.earnings, c);
      });
      deductionHeads.forEach((c) => { t.deductions[c] += earnedFor(d.deductions, c); });
      employerHeads.forEach((c) => { t.employer[c] += earnedFor(d.employer_contributions, c); });
      const grossFixed = Number(d.gross_fixed) || fixedEarningHeads.reduce((s, c) => s + fixedFor(d, c), 0);
      const gross = Number(d.gross_pay) || 0;
      const emp = Number(d.total_employer_contributions) || 0;
      t.gross_fixed += grossFixed;
      t.gross += gross;
      t.deduct += Number(d.total_deductions) || 0;
      t.net += Number(d.net_pay) || 0;
      t.employer_total += emp;
      t.final_ctc += gross + emp;
    });
    return t;
  }, [filteredDetails, fixedEarningHeads, earningHeads, deductionHeads, employerHeads]);


  const exportBifurcated = () => {
    if (!filteredDetails.length) { toast.error('No details to export'); return; }
    const rows = filteredDetails.map((d, idx) => {
      const emp = employeeMasters[d.employee_id] || employeeMasters[d.employee_code] || {};
      const codeFallback = d.employee_code
        || (d.employee_id ? `id-${String(d.employee_id).slice(-6)}` : '')
        || `(ROW-${idx + 1})`;
      
      const row = {
        Sl_No: idx + 1,
      };

      // Add enabled Employee Master detail columns
      EMP_DETAIL_COLUMNS.filter(c => selectedColumns.includes(c.id)).forEach(col => {
        if (col.id === 'account_no') {
          const bank = emp.bank_details || {};
          const fullAcc = bank.account_number || bank.account_no || bank.bank_account_number || emp.account_number || emp.bank_account_number || '';
          row[col.label] = fullAcc ? `\t${fullAcc}` : '—';
        } else {
          row[col.label] = getColValue(col.id, d, emp);
        }
      });

      // 1) Fixed Salary
      fixedEarningHeads.forEach((c) => { row[`Fixed_${c}`] = fixedFor(d, c); });
      row.Fixed_Gross = Number(d.gross_fixed) || fixedEarningHeads.reduce((s, c) => s + fixedFor(d, c), 0);
      // 2) Days
      row.Base_Days = d.base_days ?? d.total_days;
      row.Base_Source = d.base_days_source || 'calendar';
      row.Month_Days = d.total_days;
      row.LOP_Days = d.lop_days;
      row.Additional_Days = d.additional_days || 0;
      row.Payable_Days = d.payable_days;
      // 3) Earned Salary
      earningHeads.forEach((c) => {
        if (!isNonMonetaryField(c)) {
          row[`Earned_${c}`] = earnedFor(d.earnings, c);
        }
      });
      row.Earned_Gross = d.gross_pay;
      const hraVal = earnedFor(d.earnings, 'HRA') || earnedFor(d.earnings, 'HOUSE_RENT_ALLOWANCE') || Number(d.earned_hra) || 0;
      row.Earned_HRA = hraVal;
      row.PF_Wage_Basis = d.pf_wage_basis || '—';
      row.PF_Wage_Basis_Gross = d.pf_wage_basis_gross ?? 0;
      row.PF_Wage = d.pf_wage ?? 0;
      row.Fixed_Gross_For_ESI = d.fixed_gross ?? row.Fixed_Gross;
      // 4) Deductions
      deductionHeads.forEach((c) => { row[`Ded_${c}`] = earnedFor(d.deductions, c); });
      const tdsHead = (d.deductions || []).find(x => (x.head_code || '').toUpperCase() === 'TDS') || {};
      row.Calculated_TDS = tdsHead.system_tds ?? d.system_tds ?? (earnedFor(d.deductions, 'TDS') || 0);
      row.TDS_Override = tdsHead.override_tds ?? (d.tds_override_applied ? earnedFor(d.deductions, 'TDS') : '—');
      row.Effective_TDS = tdsHead.amount ?? earnedFor(d.deductions, 'TDS') ?? 0;
      row.TDS_Difference = tdsHead.difference ?? (row.Calculated_TDS - row.Effective_TDS);
      row.Total_Deductions = d.total_deductions;
      // 5) Net Pay
      row.Net_Pay = d.net_pay;
      // 6) Employer Contribution
      employerHeads.forEach((c) => { row[`Employer_${c}`] = earnedFor(d.employer_contributions, c); });
      row.Total_Employer_Contribution = d.total_employer_contributions || 0;
      // 7) Final CTC
      row.Final_CTC_Monthly = (Number(d.gross_pay) || 0) + (Number(d.total_employer_contributions) || 0);
      row.Status = d.status ? (d.hold_reason ? `${d.status} · ${d.hold_reason}` : d.status) : 'draft';
      return row;
    });
    const fname = groupFilter ? `_${groupFilter.code}` : '';
    downloadCSV(rows, `salary_register_run_${run.run_number}${fname}.csv`);
    toast.success('Transparent salary register exported with Employee Master details');
  };

  const groupedColumns = useMemo(() => {
    const map = {};
    EMP_DETAIL_COLUMNS.forEach(c => {
      if (!map[c.group]) map[c.group] = [];
      map[c.group].push(c);
    });
    return map;
  }, []);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="payroll-register">
      {(run.run_mode === 'legacy_import' || run.is_historical) && (
        <div
          data-testid="legacy-import-banner"
          className="px-4 py-3 bg-amber-50 border-b border-amber-300 text-amber-900 text-sm flex items-start gap-2"
        >
          <span className="text-lg leading-none">⚠</span>
          <div>
            <strong>Historical Import — Admin View Only.</strong>{' '}
            {run.banner ||
              'This month contains payroll imported from an actual processed file. The salary register reflects what was actually paid, not what the current engine would recompute. Variance reconciliation is available on the Legacy Imports page.'}
            <div className="text-xs mt-1 text-amber-800">
              This banner is hidden on employee payslips.
            </div>
          </div>
        </div>
      )}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">
            Run #{run.run_number} — Bifurcated Payroll Register
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Period: <span className="font-medium text-slate-700">{run.period_name || run.period || '—'}</span> · Status:{' '}
            <span className={`px-2 py-0.5 rounded-full ${statusColors[run.status] || 'bg-slate-100'}`} data-testid="run-status-badge">{run.status?.replace('_', ' ')}</span>
            {(run.statutory_month || run.statutory_year) && (() => {
              const m = Number(run.statutory_month || 0);
              const y = Number(run.statutory_year || 0);
              const fyStart = m >= 4 ? y : y - 1;
              const fyLabel = `${fyStart}-${String(fyStart + 1).slice(-2)}`;
              return (
                <>
                  {' · '}
                  <span
                    className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium"
                    title="Income-tax / TDS Financial Year used by the engine for this run."
                    data-testid="run-fy-badge"
                  >
                    FY {fyLabel}
                  </span>
                </>
              );
            })()}
            {run.processed_at && (
              <>
                {' · '}
                <span title="Timestamp of the last engine run on this payroll" data-testid="run-processed-at">
                  Last processed: <b>{new Date(run.processed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</b>
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-wrap gap-2">
          {/* Group Filter Dropdown */}
          <div className="flex items-center gap-1.5 bg-violet-50 border border-violet-200 px-2.5 py-1 rounded-lg">
            <label className="text-xs font-semibold text-violet-800">Group:</label>
            <select
              value={typeof groupFilter === 'object' && groupFilter ? groupFilter.id || groupFilter.code : (groupFilter || '')}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  setGroupFilter(null);
                } else {
                  const found = ctcGroups.find(g => g.id === val || g.code === val);
                  setGroupFilter(found || val);
                }
              }}
              data-testid="register-group-select"
              className="text-xs border-0 bg-transparent font-semibold text-violet-900 focus:ring-0 focus:outline-none cursor-pointer"
            >
              <option value="">All Groups</option>
              {ctcGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name || g.code}</option>
              ))}
            </select>
            {groupFilter && (
              <button onClick={() => setGroupFilter(null)} className="text-violet-600 hover:text-violet-900">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Quick Filter Search */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2" />
            <input
              type="text"
              placeholder="Search employee / dept / loc..."
              value={registerSearch}
              onChange={(e) => setRegisterSearch(e.target.value)}
              className="pl-8 pr-3 py-1 text-xs border border-slate-300 rounded-lg w-48 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
            />
            {registerSearch && (
              <button onClick={() => setRegisterSearch('')} className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Mask Sensitive Toggle */}
          <button
            onClick={() => setMaskSensitive(!maskSensitive)}
            className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              maskSensitive
                ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
            }`}
            title={maskSensitive ? "Showing masked numbers. Click to reveal sensitive details." : "Showing unmasked numbers. Click to mask sensitive details."}
          >
            {maskSensitive ? <Shield className="w-3.5 h-3.5 text-slate-600" /> : <ShieldOff className="w-3.5 h-3.5 text-amber-700" />}
            <span>{maskSensitive ? 'Masked' : 'Unmasked'}</span>
          </button>

          {/* Column Selector Button */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="flex items-center space-x-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-indigo-100 transition-colors"
              data-testid="column-selector-btn"
            >
              <Columns className="w-3.5 h-3.5" />
              <span>Columns ({visibleEmpDetailCols.length})</span>
              <ChevronDown className="w-3 h-3 ml-0.5" />
            </button>

            {/* Column Selector Modal Dropdown */}
            {showColumnSelector && (
              <div className="absolute right-0 top-9 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 w-80 p-4 animate-in fade-in zoom-in-95">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
                  <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <Columns className="w-4 h-4 text-indigo-600" /> Column Display Preferences
                  </h4>
                  <button onClick={() => setShowColumnSelector(false)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Filter columns..."
                    value={colSearchQuery}
                    onChange={(e) => setColSearchQuery(e.target.value)}
                    className="w-full px-2.5 py-1 text-xs border border-slate-300 rounded bg-slate-50 focus:bg-white focus:outline-none"
                  />
                </div>

                <div className="flex items-center justify-between text-[11px] mb-2 px-1">
                  <button
                    onClick={() => setSelectedColumns(EMP_DETAIL_COLUMNS.map(c => c.id))}
                    className="text-indigo-600 hover:underline font-medium"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedColumns(['emp_code', 'emp_name'])}
                    className="text-slate-500 hover:underline"
                  >
                    Deselect All
                  </button>
                  <button
                    onClick={() => setSelectedColumns(DEFAULT_SELECTED_COLS)}
                    className="text-amber-600 hover:underline font-medium"
                  >
                    Reset Defaults
                  </button>
                </div>

                <div className="max-h-64 overflow-y-auto space-y-3 text-xs pr-1 border-t border-b border-slate-100 py-2">
                  {Object.entries(groupedColumns).map(([groupName, cols]) => {
                    const filteredCols = cols.filter(c => c.label.toLowerCase().includes(colSearchQuery.toLowerCase()));
                    if (filteredCols.length === 0) return null;
                    return (
                      <div key={groupName} className="space-y-1">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 px-1 py-0.5 rounded">
                          {groupName}
                        </div>
                        {filteredCols.map(col => {
                          const isChecked = selectedColumns.includes(col.id);
                          return (
                            <label key={col.id} className="flex items-center justify-between px-1 py-1 hover:bg-slate-50 rounded cursor-pointer text-slate-700">
                              <span className="text-xs">{col.label}</span>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedColumns(prev => [...prev, col.id]);
                                  } else {
                                    setSelectedColumns(prev => prev.filter(x => x !== col.id));
                                  }
                                }}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                              />
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    onClick={() => setShowColumnSelector(false)}
                    className="px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            data-testid="export-register-btn"
            onClick={exportBifurcated}
            className="flex items-center space-x-1.5 bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-700"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /><span>Export Register</span>
          </button>
          {actionButtons.map((btn, i) => {
            const Icon = btn.icon;
            return (
              <button key={i} data-testid={`register-action-${btn.label.toLowerCase().replace(/\s/g, '-')}`} onClick={btn.action} disabled={processing || btn.disabled} className={`${btn.color} text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-90 flex items-center space-x-1 disabled:cursor-not-allowed`}>
                <Icon className="w-3 h-3" /><span>{btn.label}</span>
              </button>
            );
          })}
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700 ml-2" data-testid="register-close-btn">Close</button>
        </div>
      </div>

      {staleInfo?.is_stale && (
        <div className="bg-amber-50 border-y border-amber-300 px-4 py-3 flex items-start gap-3" data-testid="stale-banner">
          <FileSpreadsheet className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              {staleInfo.predates_mi_merge
                ? 'This run was processed before Monthly Inputs were merged.'
                : `${staleInfo.monthly_inputs_newer_than_run} monthly input row${staleInfo.monthly_inputs_newer_than_run === 1 ? ' was' : 's were'} updated after this run was last processed.`}
            </p>
            <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
              Click <strong>Reprocess</strong> to pull in the latest OT / Incentive / Other earnings &amp; deductions and refresh the register.
            </p>
          </div>
          <button
            data-testid="reprocess-btn"
            onClick={onReprocess}
            disabled={processing || !staleInfo.can_reprocess}
            className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-3 py-2 rounded-lg text-xs font-medium shrink-0"
          >
            <RotateCcw className="w-3.5 h-3.5" /> {processing ? 'Reprocessing…' : 'Reprocess with latest inputs'}
          </button>
        </div>
      )}

      {miDiag?.hint && (
        <div className="bg-blue-50 border-y border-blue-300 px-4 py-3 flex items-start gap-3" data-testid="mi-diagnostic-banner">
          <Eye className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs">
            <p className="font-semibold text-blue-900 mb-0.5">Monthly Inputs diagnostic</p>
            <p className="text-blue-900 leading-relaxed">{miDiag.hint}</p>
          </div>
        </div>
      )}

      {/* Pre-flight + Report download buttons */}
      <div className="px-4 pt-4 space-y-3">
        <PayrollPreflightPanel runId={run.id} />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-slate-50 border-b border-slate-100">
        <div className="text-center"><p className="text-xs text-slate-500">Employees</p><p className="text-lg font-bold">{filteredDetails.length}</p></div>
        <div className="text-center"><p className="text-xs text-slate-500">Total Gross</p><p className="text-lg font-bold text-emerald-600 font-mono">{fmt(totals.gross)}</p></div>
        <div className="text-center"><p className="text-xs text-slate-500">Total Deductions</p><p className="text-lg font-bold text-rose-600 font-mono">{fmt(totals.deduct)}</p></div>
        <div className="text-center"><p className="text-xs text-slate-500">Total Net</p><p className="text-lg font-bold text-[#0052FF] font-mono">{fmt(totals.net)}</p></div>
        <div className="text-center"><p className="text-xs text-slate-500">Employer Cost</p><p className="text-lg font-bold text-slate-700 font-mono">{fmt(run.total_employer_cost)}</p></div>
      </div>

      <GroupRegisterPanel runId={run.id} onSelectGroup={(g) => setGroupFilter(g)} />

      {groupFilter && (
        <div className="px-4 py-2 bg-violet-50 border-y border-violet-200 flex items-center justify-between text-xs" data-testid="register-group-filter">
          <span className="text-violet-800">
            Showing only employees in <b>{groupFilter.code}</b>
            {' '}({filteredDetails.length} of {details.length} rows)
          </span>
          <button
            onClick={() => setGroupFilter(null)}
            className="text-[11px] text-violet-700 hover:underline"
            data-testid="register-clear-group-filter"
          >
            Clear filter
          </button>
        </div>
      )}

      {filteredDetails.length === 0 ? (
        <div className="p-10 text-center text-slate-400 text-sm">
          {groupFilter ? `No rows for group ${groupFilter.code}.` : 'No details yet — process the run to see the bifurcated register.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="px-4 py-2 bg-indigo-50 border-y border-indigo-200 text-[11px] text-indigo-900 flex items-center justify-between" data-testid="register-flow-legend">
            <div>
              <b>Salary calculation flow:</b>{' '}
              <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 mx-0.5">0 · Employee Master Details</span>
              <span className="text-slate-500">→</span>
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mx-0.5">1 · Fixed Salary</span>
              <span className="text-slate-500">→</span>
              <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 mx-0.5">2 · Month/LOP/Pay Days</span>
              <span className="text-slate-500">→</span>
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 mx-0.5">3 · Earned Salary</span>
              <span className="text-slate-500">→</span>
              <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 mx-0.5">4 · Deductions</span>
              <span className="text-slate-500">→</span>
              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-[#0052FF] mx-0.5">5 · Net Pay</span>
            </div>
            <div className="text-[10px] text-indigo-700">
              Showing <b>{visibleEmpDetailCols.length}</b> employee detail columns
            </div>
          </div>
          <table className="w-full text-xs" data-testid="register-table">
            <thead>
              <tr className="bg-slate-100 text-slate-700 text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2 text-center font-semibold text-slate-800 border-r border-slate-200 bg-slate-200/70" colSpan={visibleEmpDetailCols.length} data-testid="hdr-emp-master">
                  0 · Employee Master Details ({visibleEmpDetailCols.length} columns)
                </th>
                <th className="px-3 py-2 text-center font-semibold text-amber-800 border-r border-slate-200 bg-amber-50" colSpan={fixedEarningHeads.length + 1} data-testid="hdr-fixed-salary">
                  1 · Fixed Salary (Bifurcation)
                </th>
                <th className="px-3 py-2 text-center font-semibold text-slate-700 border-r border-slate-200 bg-slate-100" colSpan={5} data-testid="hdr-days">
                  2 · Days
                </th>
                <th className="px-3 py-2 text-center font-semibold text-emerald-800 border-r border-slate-200 bg-emerald-50" colSpan={earningHeads.length + 1} data-testid="hdr-earned-salary">
                  3 · Earned Salary (Bifurcation)
                </th>
                <th className="px-3 py-2 text-center font-semibold text-rose-800 border-r border-slate-200 bg-rose-50" colSpan={deductionHeads.length + 1} data-testid="hdr-deductions">
                  4 · Deductions
                </th>
                <th className="px-3 py-2 text-center font-semibold text-[#0052FF] bg-blue-50 border-r border-slate-200" data-testid="hdr-net">
                  5 · Net Pay
                </th>
                <th className="px-3 py-2 text-center font-semibold text-sky-800 border-r border-slate-200 bg-sky-50" colSpan={Math.max(employerHeads.length, 1) + 1} data-testid="hdr-employer">
                  6 · Employer Contribution
                </th>
                <th className="px-3 py-2 text-center font-semibold text-violet-800 bg-violet-50" data-testid="hdr-final-ctc">
                  7 · Final CTC
                </th>
                <th className="px-3 py-2 text-center font-semibold text-slate-600">Status</th>
              </tr>
              <tr className="bg-slate-50 text-slate-600 text-[11px] border-b border-slate-200">
                {visibleEmpDetailCols.map((col, idx) => (
                  <th
                    key={col.id}
                    className={`px-3 py-2 text-left font-medium whitespace-nowrap bg-slate-100/60 ${
                      idx === visibleEmpDetailCols.length - 1 ? 'border-r border-slate-200 font-semibold text-slate-800' : ''
                    }`}
                    title={`${col.group} · ${col.label}`}
                  >
                    {col.label}
                  </th>
                ))}
                {fixedEarningHeads.map((c) => (
                  <th key={`h-fx-${c}`} className="px-3 py-2 text-right font-medium whitespace-nowrap text-amber-700" title={`Fixed ${nameByCode[c] || c}`} data-testid={`col-fixed-${c}`}>
                    {nameByCode[c] || c}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-semibold text-amber-800 bg-amber-50 border-r border-slate-200">Fixed Gross</th>
                <th className="px-3 py-2 text-right font-medium text-indigo-700">Base</th>
                <th className="px-3 py-2 text-right font-medium">Month</th>
                <th className="px-3 py-2 text-right font-medium">LOP</th>
                <th className="px-3 py-2 text-right font-medium text-emerald-700">Addl.</th>
                <th className="px-3 py-2 text-right font-medium border-r border-slate-200">Pay Days</th>
                {earningHeads.map((c) => (
                  <th key={`h-e-${c}`} className="px-3 py-2 text-right font-medium whitespace-nowrap text-emerald-700" title={`Earned ${nameByCode[c] || c}`} data-testid={`col-earning-${c}`}>
                    {nameByCode[c] || c}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-semibold text-emerald-800 bg-emerald-50 border-r border-slate-200">Earned Gross</th>
                {deductionHeads.map((c) => (
                  <th key={`h-d-${c}`} className="px-3 py-2 text-right font-medium whitespace-nowrap text-rose-700" title={nameByCode[c] || c} data-testid={`col-deduction-${c}`}>
                    {nameByCode[c] || c}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-semibold text-rose-800 bg-rose-50 border-r border-slate-200">Total Ded.</th>
                <th className="px-3 py-2 text-right font-semibold text-[#0052FF] bg-blue-50 border-r border-slate-200">Net Pay</th>
                {employerHeads.length === 0 ? (
                  <th className="px-3 py-2 text-right font-medium text-sky-700 italic">—</th>
                ) : employerHeads.map((c) => (
                  <th key={`h-er-${c}`} className="px-3 py-2 text-right font-medium whitespace-nowrap text-sky-700" title={nameByCode[c]} data-testid={`col-employer-${c}`}>
                    {c}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-semibold text-sky-800 bg-sky-50 border-r border-slate-200">Total Empr.</th>
                <th className="px-3 py-2 text-right font-semibold text-violet-800 bg-violet-50" data-testid="col-final-ctc">Final CTC</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDetails.map((d, i) => {
                const emp = employeeMasters[d.employee_id] || employeeMasters[d.employee_code] || {};
                const grossFixed = Number(d.gross_fixed) || fixedEarningHeads.reduce((s, c) => s + fixedFor(d, c), 0);
                const totalEmp = Number(d.total_employer_contributions) || 0;
                const finalCtc = (Number(d.gross_pay) || 0) + totalEmp;
                return (
                  <tr key={i} className={`hover:bg-slate-50 ${d.status === 'exception' ? 'bg-rose-50/40' : d.status === 'on_hold' ? 'bg-amber-50/60' : d.monthly_input_applied ? 'bg-indigo-50/30' : ''}`} data-testid={`register-row-${d.employee_code || i}`}>
                    {visibleEmpDetailCols.map((col, idx) => {
                      const val = getColValue(col.id, d, emp);
                      const isLast = idx === visibleEmpDetailCols.length - 1;
                      return (
                        <td
                          key={col.id}
                          className={`px-3 py-2 whitespace-nowrap ${
                            col.id === 'emp_code' ? 'font-mono font-medium text-slate-800' :
                            col.id === 'emp_name' ? 'font-medium text-slate-900' :
                            col.id === 'monthly_ctc' ? 'font-mono text-emerald-700 text-right' :
                            'text-slate-600'
                          } ${isLast ? 'border-r border-slate-200' : ''}`}
                        >
                          {val}
                        </td>
                      );
                    })}

                    {/* Fixed Salary */}
                    {fixedEarningHeads.map((c) => {
                      const v = fixedFor(d, c);
                      return (
                        <td key={`r-fx-${c}`} className={`px-3 py-2 text-right font-mono ${v ? 'text-amber-800' : 'text-slate-300'}`} data-testid={`cell-fixed-${d.employee_code}-${c}`}>
                          {v ? fmt(v) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-900 bg-amber-50/40 border-r border-slate-200">{fmt(grossFixed)}</td>

                    {/* Days */}
                    <td className="px-3 py-2 text-right text-indigo-700 font-mono font-semibold" data-testid={`cell-base-${d.employee_code}`}>{d.base_days ?? d.total_days ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-700 font-mono">{d.total_days ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-rose-700 font-mono">{d.lop_days ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-mono" data-testid={`cell-addl-${d.employee_code}`}>{Number(d.additional_days) > 0 ? `+${d.additional_days}` : '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-800 font-mono font-semibold border-r border-slate-200">{d.payable_days ?? '—'}</td>

                    {/* Earned Salary */}
                    {earningHeads.map((c) => {
                      const v = earnedFor(d.earnings, c);
                      return (
                        <td key={`r-e-${c}`} className={`px-3 py-2 text-right font-mono ${v ? 'text-emerald-700' : 'text-slate-300'}`} data-testid={`cell-earning-${d.employee_code}-${c}`}>
                          {v ? fmt(v) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-800 bg-emerald-50/40 border-r border-slate-200">{fmt(d.gross_pay)}</td>

                    {/* Deductions */}
                    {deductionHeads.map((c) => {
                      const v = earnedFor(d.deductions, c);
                      return (
                        <td key={`r-d-${c}`} className={`px-3 py-2 text-right font-mono ${v ? 'text-rose-700' : 'text-slate-300'}`} data-testid={`cell-deduction-${d.employee_code}-${c}`}>
                          {v ? fmt(v) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-mono font-semibold text-rose-800 bg-rose-50/40 border-r border-slate-200">{fmt(d.total_deductions)}</td>

                    {/* Net */}
                    <td className="px-3 py-2 text-right font-mono font-bold text-[#0052FF] bg-blue-50/40 border-r border-slate-200">{fmt(d.net_pay)}</td>

                    {/* Employer Contributions */}
                    {employerHeads.length === 0 ? (
                      <td className="px-3 py-2 text-right text-slate-300">—</td>
                    ) : employerHeads.map((c) => {
                      const v = earnedFor(d.employer_contributions, c);
                      return (
                        <td key={`r-er-${c}`} className={`px-3 py-2 text-right font-mono ${v ? 'text-sky-700' : 'text-slate-300'}`} data-testid={`cell-employer-${d.employee_code}-${c}`}>
                          {v ? fmt(v) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-mono font-semibold text-sky-800 bg-sky-50/40 border-r border-slate-200">{fmt(totalEmp)}</td>

                    {/* Final CTC */}
                    <td className="px-3 py-2 text-right font-mono font-bold text-violet-800 bg-violet-50/40" data-testid={`cell-final-ctc-${d.employee_code}`}>{fmt(finalCtc)}</td>
                    <td className="px-3 py-2">
                      <span
                        title={d.monthly_input_remarks || ''}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          d.status === 'exception' ? 'bg-rose-100 text-rose-700' :
                          ['HOLD', 'on_hold', 'hold', 'payment_hold'].includes(d.status) ? 'bg-amber-100 text-amber-800' :
                          d.status === 'NOTICE' ? 'bg-purple-100 text-purple-800 font-semibold' :
                          ['CALCULATED', 'calculated'].includes(d.status) ? 'bg-emerald-100 text-emerald-700' :
                          'bg-indigo-100 text-indigo-700'
                        }`}
                      >
                        {d.status}{d.hold_reason ? ` · ${d.hold_reason}` : ''}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {/* Grand total row */}
              <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300" data-testid="register-totals-row">
                <td className="px-3 py-2 text-slate-900 border-r border-slate-200" colSpan={visibleEmpDetailCols.length}>
                  TOTAL ({filteredDetails.length} employees{groupFilter ? ` · ${groupFilter.code}` : ''})
                </td>
                {fixedEarningHeads.map((c) => (
                  <td key={`t-fx-${c}`} className="px-3 py-2 text-right font-mono text-amber-800">{fmt(totals.fixed[c])}</td>
                ))}
                <td className="px-3 py-2 text-right font-mono font-bold text-amber-900 bg-amber-100 border-r border-slate-200">{fmt(totals.gross_fixed)}</td>
                <td colSpan={5} className="border-r border-slate-200" />
                {earningHeads.map((c) => (
                  <td key={`t-e-${c}`} className="px-3 py-2 text-right font-mono text-emerald-800">{fmt(totals.earnings[c])}</td>
                ))}
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-900 bg-emerald-100 border-r border-slate-200">{fmt(totals.gross)}</td>
                {deductionHeads.map((c) => (
                  <td key={`t-d-${c}`} className="px-3 py-2 text-right font-mono text-rose-800">{fmt(totals.deductions[c])}</td>
                ))}
                <td className="px-3 py-2 text-right font-mono font-bold text-rose-900 bg-rose-100 border-r border-slate-200">{fmt(totals.deduct)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[#0052FF] bg-blue-100 border-r border-slate-200">{fmt(totals.net)}</td>
                {employerHeads.length === 0 ? (
                  <td className="px-3 py-2 text-right text-slate-300">—</td>
                ) : employerHeads.map((c) => (
                  <td key={`t-er-${c}`} className="px-3 py-2 text-right font-mono text-sky-800">{fmt(totals.employer[c])}</td>
                ))}
                <td className="px-3 py-2 text-right font-mono font-bold text-sky-900 bg-sky-100 border-r border-slate-200">{fmt(totals.employer_total)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-violet-900 bg-violet-100" data-testid="total-final-ctc">{fmt(totals.final_ctc)}</td>
                <td />
              </tr>
            </tbody>
          </table>
          <p className="text-[11px] text-slate-500 px-4 py-2 bg-slate-50 border-t border-slate-100">
            <b>Fixed Salary</b> is the un-prorated monthly value per head. <b>Earned Salary</b> = Fixed × Payable/Month Days (per the head's proration basis). <b>Final CTC</b> = Earned Gross + Employer Contribution. Empty cells (—) mean the field is not available for that employee.
          </p>
        </div>
      )}
    </div>
  );
};
